// @ts-nocheck
import { CustomWidgetModule } from "@store/Framework/Types/types";

import React from "react";
import { css } from "styled-components";

import translate from "@insite/client-framework/Translate";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingOverlay } from "@insite/mobius/index";
import useIsAdmin from "@store/Framework/Common/Hooks/useIsAdmin";
import { CustomerPoNumber } from "@store/Framework/Services/CustomerPoNumberService";
import ManageDefaultPoActions from "@store/Library/Widgets/ManageDefaultPO/ManageDefaultPoActions";
import ManageDefaultPODisplay from "@store/Library/Widgets/ManageDefaultPO/ManageDefaultPODisplay";
import useManageDefaultPO from "@store/Library/Widgets/ManageDefaultPO/useManageDefaultPO";
import GridContainer from "@store/MobiusOverrides/GridContainer/GridContainer";
import GridItem from "@store/MobiusOverrides/GridItem/GridItem";
import Typography, { TypographyProps } from "@store/MobiusOverrides/Typography/Typography";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

const mobileQuery = getMediaQuery({ option: "down", first: true, min: 0, max: 700, last: false });

export interface ManageDefaultPOSelectorStyles {
    container?: GridContainerProps;
    subtitleGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    tableGridItem?: GridItemProps;
}

interface PoOverrideSelectorProps {
    extendedStyles?: ManageDefaultPOSelectorStyles;
    overrideStyles?: ManageDefaultPOSelectorStyles;
}

const manageDefaultPOSelectorStyles: ManageDefaultPOSelectorStyles = {
    tableGridItem: {
        width: [12, 12, 12, 11, 11],
        css: css`
            padding-top: ${pxToRem(8)};
        `,
    },
    subtitleGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    titleText: {
        variant: "h2",
        as: "h2",
        css: css`
            ${mobileQuery} {
                font-size: 1.25rem;
                text-align: center;
            }
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            ${mobileQuery} {
                padding-block: 1rem;
            }
        `,
    },
    container: {
        css: css`
            flex-direction: column;
        `,
        offsetCss: css`
            display: flex;
            flex-direction: column;
            row-gap: 1rem;
        `,
    },
};

export const ManageDefaultPO: React.FC<PoOverrideSelectorProps> = ({ extendedStyles, overrideStyles }) => {
    const styles = useExpandProps(manageDefaultPOSelectorStyles, extendedStyles, overrideStyles);
    const [newPo, setNewPo] = React.useState<CustomerPoNumber>();
    const [searchText, setSearchText] = React.useState<string>("");
    const [editingPo, setEditingPo] = React.useState<CustomerPoNumber>();
    const isAdmin = useIsAdmin();
    const {
        createPoNumber,
        selectedOverride,
        setSelectedOverride,
        validateStartDate,
        validateEndDate,
        deletePoNumber,
        updatePoNumber,
        loading,
        billToPos,
        shipToPos,
        billTo,
        shipTo,
    } = useManageDefaultPO();

    if (!isAdmin) {
        return null;
    }
    const handleDelete = (poId: string) => {
        if (selectedOverride) {
            deletePoNumber(poId, selectedOverride);
        }
    };

    const handleNewPo = () => {
        setNewPo({
            customerId: selectedOverride === "billTo" ? billTo.id : shipTo.id,
            startDate: new Date(),
            endDate: null,
            poNumber: "",
            properties: {},
            uri: "",
            id: "",
        });
    };
    const handleSave = async (po: CustomerPoNumber | null) => {
        if (!po) {
            setNewPo(undefined);
            setEditingPo(undefined);
            return;
        }
        if (newPo && selectedOverride) {
            const { id, ...newPoWithoutId } = po;
            await createPoNumber(newPoWithoutId, selectedOverride);
            setNewPo(undefined);
        } else if (editingPo && selectedOverride) {
            await updatePoNumber(po, selectedOverride);
            setEditingPo(undefined);
        }
    };
    if (!billTo || !shipTo) {
        return null;
    }
    let customerPos = selectedOverride ? (selectedOverride === "billTo" ? billToPos : shipToPos) : [];

    if (searchText) {
        customerPos = customerPos.filter(po => po.poNumber.toLowerCase().includes(searchText.toLowerCase()));
    }
    // sort by date descending so that the most recent is first
    customerPos.sort((a, b) => {
        return b.startDate.getTime() - a.startDate.getTime();
    });

    const activePoId = customerPos.find(po => {
        const now = new Date();
        return po.startDate <= now && (!po.endDate || po.endDate >= now);
    })?.id;
    if (newPo) {
        customerPos = [newPo, ...customerPos];
    }

    return (
        <LoadingOverlay loading={loading}>
            <GridContainer gap={48} {...styles.container}>
                <GridItem {...styles.titleGridItem}>
                    <Typography {...styles.titleText} data-test-selector="default_PO_Title">
                        {translate("Manage Default Purchase Order Number")}
                    </Typography>
                </GridItem>
                <ManageDefaultPoActions
                    billTo={billTo}
                    shipTo={shipTo}
                    handleNewPo={handleNewPo}
                    setSearchText={setSearchText}
                    editingPo={editingPo}
                    newPo={newPo}
                    searchText={searchText}
                    selectedOverride={selectedOverride}
                    setSelectedOverride={setSelectedOverride}
                />

                <GridItem {...styles.tableGridItem}>
                    <ManageDefaultPODisplay
                        isNew={!!newPo}
                        activePoId={activePoId}
                        validateStartDate={(startDate: Date, endDate: Date | null) =>
                            validateStartDate(startDate, endDate, selectedOverride!, editingPo?.id || "")
                        }
                        validateEndDate={(startDate: Date, endDate: Date | null) =>
                            validateEndDate(startDate, endDate, selectedOverride!, editingPo?.id || "")
                        }
                        onDelete={handleDelete}
                        onSave={handleSave}
                        editingPo={newPo || editingPo}
                        setEditingPo={setEditingPo}
                        customerPOs={customerPos}
                    />
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: CustomWidgetModule = {
    component: ManageDefaultPO,
    definition: {
        group: "Basic",
    },
};

export default widgetModule;
